import { PendingPaymentResponseParams } from "@payment-client/interfaces/OrderPublic";
import { isEligibleForNewPendingPaymentFlow, isEligibleForPublicApi } from "./publicOrderExtensions";
import { getMarketplaceBusinessLine, getMarketplaceCategory } from "@payment-client/utils/Headers";
import { V3_ENDPOINTS, APP_LAUNCH_CONFIRM_ORDER_URI } from "@payment-client/constants/Order";
import { getSwiggyHeaders } from "@swiggy-private/checkout-core";
import { Headers } from "@payment-client/interfaces/Http";
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_CATEGORY } from "@swiggy-private/payment-types";
import { DOWNSTREAM_HOSTS } from "@payment-client/constants/Hosts";
import { format } from "@swiggy-private/js-utils";

// Direct BE endpoints
export const BE_ENDPOINTS = {
    CONFIRM_EDIT_ORDER_URI: "api/v3/order/edit/confirm",
    CONFIRM_ORDER_URI: "api/v2/order/confirm",
};

function removeLeadingSlash(endpoint: string): string {
    return endpoint.startsWith("/") ? endpoint.slice(1) : endpoint;
}

// TODO: Need to remove this helper since pointing to chkout for pending payment params will not be required in future
export function getCheckoutConfirmOrderEndpoint(businessLine: string): string {
    // not handled dash/dineout flow as older flow is not applicable for dash
    switch (businessLine) {
        case MARKETPLACE_BUSINESS_LINE.EDIT_ORDER:
        case MARKETPLACE_BUSINESS_LINE.POST_ORDER_TIP:
        case MARKETPLACE_BUSINESS_LINE.PAY_ON_DELIVERY:
            return BE_ENDPOINTS.CONFIRM_EDIT_ORDER_URI;
    }
    return BE_ENDPOINTS.CONFIRM_ORDER_URI;
}

export function getConfirmOrderEndpoint(
    businessLine: string,
    category: string,
    bodyParams: { [key: string]: unknown },
): string {
    switch (businessLine) {
        case MARKETPLACE_BUSINESS_LINE.EDIT_ORDER:
        case MARKETPLACE_BUSINESS_LINE.POST_ORDER_TIP:
        case MARKETPLACE_BUSINESS_LINE.PAY_ON_DELIVERY:
            return removeLeadingSlash(V3_ENDPOINTS.CONFIRM_EDIT_ORDER_URI);

        case MARKETPLACE_BUSINESS_LINE.SWIGGY_SUPER:
            return removeLeadingSlash(`${V3_ENDPOINTS.SUPER_CONFIRM_ORDER_URI}?order_id=${bodyParams?.orderId}`);

        case MARKETPLACE_BUSINESS_LINE.SWIGGY_ONE:
            return removeLeadingSlash(V3_ENDPOINTS.CONFIRM_ORDER_URI);

        case MARKETPLACE_BUSINESS_LINE.DASH:
            if (category === MARKETPLACE_CATEGORY.INSTAMART) {
                return removeLeadingSlash(V3_ENDPOINTS.NEW_CONFIRM_DASH_ORDER);
            }
            return removeLeadingSlash(format(V3_ENDPOINTS.DASH_ORDER_DETAILS_URI, bodyParams?.orderId));
        case MARKETPLACE_BUSINESS_LINE.DINEOUT:
            return removeLeadingSlash(V3_ENDPOINTS.DINEOUT_CONFIRM_ORDER_URI);

        case MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY:
            return removeLeadingSlash(V3_ENDPOINTS.SWIGGY_MONEY_ORDER_URI);

        case MARKETPLACE_BUSINESS_LINE.ASSURE:
            return removeLeadingSlash(V3_ENDPOINTS.NEW_CONFIRM_DASH_ORDER);

        default:
            return removeLeadingSlash(V3_ENDPOINTS.CONFIRM_ORDER_URI);
    }
}

export function getPendingPaymentConfirmRequestObj({
    method,
    headers,
    body,
    isConfirmOnAppLaunchEnabled = false,
}: {
    method: string;
    headers: Headers;
    body?: { [key: string]: unknown };
    isConfirmOnAppLaunchEnabled?: boolean;
}): PendingPaymentResponseParams {
    /*
     * Context: older flows below a particular native version(which do not have required app changes) will work the way they do currently,
     * in those cases we will send them the current pending payment params payload(pointed to chkout.swiggy.com)
     * and for versions where required app changes are available, we will point use updated payload(pointed to webpayments host)
     */
    const webPaymentsHost = DOWNSTREAM_HOSTS.API_HOST;
    const checkoutHost = DOWNSTREAM_HOSTS.CHECKOUT_READ_HOST;

    const isEligibleForNewFlow = isEligibleForNewPendingPaymentFlow(
        headers["platform"] as string,
        headers["version-code"] as string,
    );

    const isEligibleForNewPublicApi = isEligibleForPublicApi(
        headers["platform"] as string,
        headers["version-code"] as string,
    );

    const businessLine = getMarketplaceBusinessLine(headers);
    const category = getMarketplaceCategory(headers);

    let endpoint: string;

    if (isEligibleForNewPublicApi && isConfirmOnAppLaunchEnabled) {
        endpoint = getConfirmOrderEndpoint(businessLine, category, body ?? {});
    } else if (isEligibleForNewFlow) {
        endpoint = APP_LAUNCH_CONFIRM_ORDER_URI;
    } else {
        endpoint = getCheckoutConfirmOrderEndpoint(businessLine);
    }

    const host = isEligibleForNewFlow ? webPaymentsHost : checkoutHost;

    const reqParams: PendingPaymentResponseParams = {
        method,
        endpoint,
        host,
        headers: getSwiggyHeaders(headers),
        body: isEligibleForNewFlow ? body : undefined,
    };

    return reqParams;
}
